<template>
    <div
        :class="[
            type === 'textarea' ? 'pt-textarea' : 'pt-input',
            inputSize ? 'pt-input--' + inputSize : '',
            {
                'is-disabled': inputDisabled,
                'is-exceed': inputExceed,
                'is-focus': focused,
                'pt-input-group': $slots.prepend || $slots.append,
                'pt-input-group--append': $slots.append,
                'pt-input-group--prepend': $slots.prepend,
                'pt-input--prefix': $slots.prefix || prefixIcon,
                'pt-input--suffix': $slots.suffix || suffixIcon || clearable || showPassword,
                'pt-input--suffix--password-clear': clearable && showPassword,
            },
            `pt-input--${theme}`,
            `pt-input--mode-${mode}`,
            $attrs.class,
        ]"
        :style="$attrs.style"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <template v-if="type !== 'textarea'">
            <!-- 前置元素 -->
            <div v-if="$slots.prepend" class="pt-input-group__prepend">
                <slot name="prepend"></slot>
            </div>
            <input
                v-if="type !== 'textarea'"
                ref="input"
                class="pt-input__inner"
                v-bind="ptAttrs"
                :type="showPassword ? (passwordVisible ? 'text' : 'password') : type"
                :disabled="inputDisabled"
                :readonly="readonly"
                :autocomplete="autocomplete"
                :tabindex="tabindex"
                :aria-label="label"
                :placeholder="placeholder"
                :style="inputStyle"
                @compositionstart="handleCompositionStart"
                @compositionupdate="handleCompositionUpdate"
                @compositionend="handleCompositionEnd"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @change="handleChange"
                @keydown="handleKeydown"
            />
            <!-- 前置内容 -->
            <span v-if="$slots.prefix || prefixIcon" class="pt-input__prefix">
                <slot name="prefix"></slot>
                <pt-icon v-if="prefixIcon" :class="['pt-input__icon', prefixIcon]" :icon="prefixIcon"></pt-icon>
            </span>
            <!-- 后置内容 -->
            <span v-if="getSuffixVisible()" class="pt-input__suffix">
                <span class="pt-input__suffix-inner">
                    <template v-if="!showClear || !showPwdVisible || !isWordLimitVisible">
                        <slot name="suffix"></slot>
                        <pt-icon v-if="suffixIcon" :class="['pt-input__icon', suffixIcon]" :icon="suffixIcon"></pt-icon>
                    </template>
                    <pt-icon
                        v-if="showClear"
                        class="pt-input__icon pt-icon-circle-close pt-input__clear"
                        icon="pt-icon-clear"
                        @mousedown.prevent
                        @click="clear"
                    ></pt-icon>
                    <pt-icon
                        v-if="showPwdVisible"
                        class="pt-input__icon pt-icon-view pt-input__clear"
                        icon="pt-icon-eyeopen"
                        @click="handlePasswordVisible"
                    ></pt-icon>
                    <span v-if="isWordLimitVisible" class="pt-input__count">
                        <span class="pt-input__count-inner"> {{ textLength }}/{{ upperLimit }} </span>
                    </span>
                </span>
                <i v-if="validateState" :class="['pt-input__icon', 'pt-input__validateIcon', validateIcon]"></i>
            </span>
            <!-- 后置元素 -->
            <div v-if="$slots.append" class="pt-input-group__append">
                <slot name="append"></slot>
            </div>
        </template>
        <textarea
            v-else
            ref="textarea"
            class="pt-textarea__inner"
            v-bind="ptAttrs"
            :tabindex="tabindex"
            :disabled="inputDisabled"
            :readonly="readonly"
            :autocomplete="autocomplete"
            :style="computedTextareaStyle"
            :aria-label="label"
            :placeholder="placeholder"
            @compositionstart="handleCompositionStart"
            @compositionupdate="handleCompositionUpdate"
            @compositionend="handleCompositionEnd"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="handleChange"
            @keydown="handleKeydown"
        >
        </textarea>
        <span v-if="isWordLimitVisible && type === 'textarea'" class="pt-input__count"
            >{{ textLength }}/{{ upperLimit }}</span
        >
    </div>
</template>

<script lang="ts">
export default {
    name: 'PtInput',
};
</script>

<script setup lang="ts">
import {
    defineComponent,
    useSlots,
    useAttrs,
    inject,
    computed,
    watch,
    nextTick,
    getCurrentInstance,
    ref,
    shallowRef,
    onMounted,
    onUpdated,
} from 'vue';
import usePtAttrs from '../../hooks/use-attrs';
import { UPDATE_MODEL_EVENT, VALIDATE_STATE_MAP } from '../../../utils/constants';
import { isObject, useGlobalConfig } from '../../../utils/util';
import isServer from '../../../utils/isServer';
import { isKorean } from '../../../utils/isDef';
import { elFormKey, elFormItemKey } from '../../form';
import calcTextareaHeight from './calcTextareaHeight';
import type { PtFormContext, PtFormItemContext } from '../../form';

type AutosizeProp =
    | {
          minRows?: number;
          maxRows?: number;
      }
    | boolean;

const PENDANT_MAP = {
    suffix: 'append',
    prefix: 'prepend',
};

interface Props {
    modelValue?: string | number;
    type?: string;
    size?: 'xlarge' | 'large' | 'medium' | 'small' | 'mini';
    resize?: 'none' | 'both' | 'horizontal' | 'vertical';
    theme?: 'underline' | 'outline';
    mode?: 'white' | 'dark';
    autosize?:
        | boolean
        | {
              minRows?: number;
              maxRows?: number;
          };
    autocomplete?: 'on' | 'off';
    placeholder?: string;
    form?: string;
    disabled?: boolean;
    readonly?: boolean;
    clearable?: boolean;
    showPassword?: boolean;
    showWordLimit?: boolean;
    suffixIcon?: string;
    prefixIcon?: string;
    label?: string;
    tabindex?: number | string;
    validateEvent?: boolean;
    inputStyle?: CSSProperties;
}

const props = withDefaults(defineProps<Props>(), {
    type: 'text',
    theme: 'outline',
    mode: 'white',
    autosize: () => false,
    autocomplete: 'off',
    disabled: false,
    readonly: false,
    clearable: false,
    showPassword: false,
    showWordLimit: false,
    validateEvent: true,
    inputStyle: () => ({}),
});

const emit = defineEmits([
    UPDATE_MODEL_EVENT,
    'input',
    'change',
    'focus',
    'blur',
    'clear',
    'mouseleave',
    'mouseenter',
    'keydown',
]);

const instance = getCurrentInstance();
const ptAttrs = usePtAttrs();
const attrs = useAttrs();
const slots = useSlots();
const $ELEMENT = useGlobalConfig();

const elForm = inject(elFormKey, {} as PtFormContext);
const elFormItem = inject(elFormItemKey, {} as PtFormItemContext);

const input = ref(null);
const textarea = ref(null);
const focused = ref(false);
const hovering = ref(false);
const isComposing = ref(false);
const passwordVisible = ref(false);
const _textareaCalcStyle = shallowRef(props.inputStyle);

const inputOrTextarea = computed(() => input.value || textarea.value);
// @TODO ===========
const inputSize = computed(() => props.size || elFormItem.size || $ELEMENT.size);
const needStatusIcon = computed(() => elForm.statusIcon);
const validateState = computed(() => elFormItem.validateState || '');
// const inputSize = computed(() => props.size || $ELEMENT.size)
// const needStatusIcon = computed(() => false)
// const validateState = computed(() =>'')
// @TODO ===========

const validateIcon = computed(() => VALIDATE_STATE_MAP[validateState.value]);
const computedTextareaStyle = computed(() => ({
    ..._textareaCalcStyle.value,
    resize: props.resize,
}));
// @TODO ===========
const inputDisabled = computed(() => props.disabled || elForm.disabled);
// const inputDisabled = computed(() => props.disabled || false)
// @TODO ===========

const nativeInputValue = computed(() =>
    props.modelValue === null || props.modelValue === undefined ? '' : String(props.modelValue),
);
const upperLimit = computed(() => attrs.maxlength);
const showClear = computed(() => {
    return (
        props.clearable &&
        !inputDisabled.value &&
        !props.readonly &&
        nativeInputValue.value &&
        (focused.value || hovering.value)
    );
});
const showPwdVisible = computed(() => {
    return props.showPassword && !inputDisabled.value && !props.readonly && (!!nativeInputValue.value || focused.value);
});
const isWordLimitVisible = computed(() => {
    return (
        props.showWordLimit &&
        attrs.maxlength &&
        (props.type === 'text' || props.type === 'textarea') &&
        !inputDisabled.value &&
        !props.readonly &&
        !props.showPassword
    );
});
const textLength = computed(() => {
    return typeof props.modelValue === 'number' ? String(props.modelValue).length : (props.modelValue || '').length;
});
const inputExceed = computed(() => {
    // show exceed style if length of initial value greater then maxlength
    return isWordLimitVisible.value && textLength.value > upperLimit.value;
});

const resizeTextarea = () => {
    const { type, autosize } = props;

    if (isServer || type !== 'textarea') return;

    if (autosize) {
        const minRows = isObject(autosize) ? autosize.minRows : void 0;
        const maxRows = isObject(autosize) ? autosize.maxRows : void 0;
        _textareaCalcStyle.value = {
            ...props.inputStyle,
            ...calcTextareaHeight(textarea.value, minRows, maxRows),
        };
    } else {
        _textareaCalcStyle.value = {
            ...props.inputStyle,
            minHeight: calcTextareaHeight(textarea.value).minHeight,
        };
    }
};

const setNativeInputValue = () => {
    const input = inputOrTextarea.value;
    if (!input || input.value === nativeInputValue.value) return;
    input.value = nativeInputValue.value;
};

const calcIconOffset = (place: string) => {
    if (!instance) return;
    const { el } = instance.vnode;
    const elList: HTMLSpanElement[] = Array.from(el.querySelectorAll(`.pt-input__${place}`));
    const target = elList.find((item) => item.parentNode === el);

    if (!target) return;

    const pendant = PENDANT_MAP[place];

    if (slots[pendant]) {
        target.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${
            el.querySelector(`.pt-input-group__${pendant}`).offsetWidth
        }px)`;
    } else {
        target.removeAttribute('style');
    }
};

const updateIconOffset = () => {
    calcIconOffset('prefix');
    calcIconOffset('suffix');
};

const handleInput = (event) => {
    const { value } = event.target;

    // should not emit input during composition
    // see: https://github.com/ElemeFE/element/issues/10516
    if (isComposing.value) return;

    // hack for https://github.com/ElemeFE/element/issues/8548
    // should remove the following line when we don't support IE
    if (value === nativeInputValue.value) return;

    emit(UPDATE_MODEL_EVENT, value);
    emit('input', value);

    // ensure native input value is controlled
    // see: https://github.com/ElemeFE/element/issues/12850
    nextTick(setNativeInputValue);
};

const handleChange = (event) => {
    emit('change', event.target.value);
};

const focus = () => {
    // see: https://github.com/ElemeFE/element/issues/18573
    nextTick(() => {
        inputOrTextarea.value.focus();
    });
};

const blur = () => {
    inputOrTextarea.value.blur();
};

const handleFocus = (event) => {
    focused.value = true;
    emit('focus', event);
};

const handleBlur = (event) => {
    focused.value = false;
    emit('blur', event);
    if (props.validateEvent) {
        elFormItem.formItemMitt?.emit('pt.form.blur', [props.modelValue]);
    }
};

const select = () => {
    inputOrTextarea.value.select();
};

const handleCompositionStart = () => {
    isComposing.value = true;
};

const handleCompositionUpdate = (event) => {
    const text = event.target.value;
    const lastCharacter = text[text.length - 1] || '';
    isComposing.value = !isKorean(lastCharacter);
};

const handleCompositionEnd = (event) => {
    if (isComposing.value) {
        isComposing.value = false;
        handleInput(event);
    }
};

const clear = () => {
    emit(UPDATE_MODEL_EVENT, '');
    emit('change', '');
    emit('clear');
};

const handlePasswordVisible = () => {
    passwordVisible.value = !passwordVisible.value;
    focus();
};

const getSuffixVisible = () => {
    return (
        slots.suffix ||
        props.suffixIcon ||
        showClear.value ||
        props.showPassword ||
        isWordLimitVisible.value ||
        (validateState.value && needStatusIcon.value)
    );
};

watch(
    () => props.modelValue,
    (val) => {
        nextTick(resizeTextarea);
        if (props.validateEvent) {
            elFormItem.formItemMitt?.emit('pt.form.change', [val]);
        }
    },
);

// native input value is set explicitly
// do not use v-model / :value in template
// see: https://github.com/ElemeFE/element/issues/14521
watch(nativeInputValue, () => {
    setNativeInputValue();
});

// when change between <input> and <textarea>,
// update DOM dependent value and styles
// https://github.com/ElemeFE/element/issues/14857
watch(
    () => props.type,
    () => {
        nextTick(() => {
            setNativeInputValue();
            resizeTextarea();
            updateIconOffset();
        });
    },
);

onMounted(() => {
    setNativeInputValue();
    updateIconOffset();
    nextTick(resizeTextarea);
});

onUpdated(() => {
    nextTick(updateIconOffset);
});

const onMouseLeave = (e) => {
    hovering.value = false;
    emit('mouseleave', e);
};

const onMouseEnter = (e) => {
    hovering.value = true;
    emit('mouseenter', e);
};

const handleKeydown = (e) => {
    emit('keydown', e);
};
defineExpose({
    input,
    inputOrTextarea,
    focus,
});
</script>
<style lang="scss" src="../style/input.scss"></style>
